import {
  Checkbox,
  FormControlLabel,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  OutlinedInput,
} from "@material-ui/core"
import FileCopyOutlinedIcon from "@material-ui/icons/FileCopyOutlined"
import FullscreenIcon from "@material-ui/icons/Fullscreen"
import MoreHorizIcon from "@material-ui/icons/MoreHoriz"
import MediaIcon from "@material-ui/icons/PermMedia"
import RemoveCircleOutlineIcon from "@material-ui/icons/RemoveCircleOutline"
import classnames from "clsx"
import dayjs from "dayjs"
import { motion, usePresence } from "framer-motion"
import { isEmpty } from "lodash"
import PropTypes from "prop-types"
import { useContext, useState } from "react"
import TimeField from "react-simple-timefield"
import BottomVideoBannerIcon from "~/assets/Icons/bottom-video-banner-icon.svg?react"
import CheckIcon from "~/assets/Icons/check-icon.svg?react"
import ResumeIcon from "~/assets/Icons/chess-icon.svg?react"
import NotesIcon from "~/assets/Icons/pen-icon.svg?react"
import SentimentQuestionIcon from "~/assets/Icons/sentiment-icon.svg?react"
import TopVideoBannerIcon from "~/assets/Icons/top-video-banner-icon.svg?react"
import AppContext from "~/contexts/App"
import LearningCuesContext from "~/contexts/LearningCues"
import VideoContext from "~/contexts/Video"
import useLearningCueMediaFileName from "~/hooks/useLearningCueMediaFileName"

import Box from "~/components/Common/Box"
import LearningCueChip from "./LearningCueChip"
import useStyles from "./styles"

const transition = { type: "spring", stiffness: 500, damping: 50, mass: 1 }

const LearningCueItem = ({
  learningCue,
  updateTime,
  lockdown,
  sameTimestampCue,
}) => {
  const classes = useStyles()

  const [isPresent, safeToRemove] = usePresence()

  const [internalValue, setInternalValue] = useState("")

  const animations = {
    layout: true,
    initial: "out",
    style: {
      position: isPresent ? "static" : "absolute",
    },
    animate: isPresent ? "in" : "out",
    whileTap: "tapped",
    variants: {
      in: { scaleY: 1 },
      out: { scaleY: 0, zIndex: -1 },
    },
    onAnimationComplete: () => !isPresent && safeToRemove(),
    transition,
  }

  const { openDialog } = useContext(AppContext)
  const {
    lesson,
    changeCheckedValue,
    setLearningCueContent,
    removeLearningCue,
    copyLearningCue,
    setHighlightedLearningCue,
    learningCuesQuery,
  } = useContext(LearningCuesContext)
  const { videoProgress, duration } = useContext(VideoContext)

  const [rowFocused, setRowFocused] = useState(false)

  const handleMediaChipClick = () => {
    if (!lockdown || (lockdown && learningCue?.media)) {
      const data = {
        ...learningCue?.media,
        lockdown: lockdown,
        lessonVideoDuration: duration,
        videoProgress: videoProgress,
        startSeconds: learningCue?.start_seconds,
      }
      openDialog("mediaUpload", data).then((res) => {
        setLearningCueContent(learningCue.id, {
          media: res,
          displaySecondsMax: duration - learningCue.start_seconds,
        })
      })
    }
  }

  const handleKnowledgeCheckChipClick = () => {
    if (!lockdown || (lockdown && learningCue?.knowledgeCheck)) {
      openDialog("knowledgeCheckEditor", {
        learningCueId: learningCue.id,
        ...learningCue.knowledgeCheck,
        lockdown: lockdown,
        startSeconds: learningCue?.start_seconds,
      }).then((values) => {
        setLearningCueContent(learningCue.id, {
          knowledgeCheck: values,
          force_pause: true,
        })
      })
    }
  }

  const handleSentimentQuestionClick = () => {
    if (!lockdown || (lockdown && learningCue?.sentimentQuestions)) {
      openDialog("sentimentQuestionsEditor", {
        learningCueId: learningCue.id,
        questions: learningCue.sentimentQuestions,
        lockdown: lockdown,
        startSeconds: learningCue?.start_seconds,
      }).then((values) => {
        setLearningCueContent(learningCue.id, {
          sentimentQuestions: values.questions,
          force_pause: true,
        })
      })
    }
  }

  const handleTopVideoBannerChipClick = () => {
    if (!lockdown || (lockdown && learningCue?.topBanner)) {
      const data = {
        ...learningCue?.topBanner,
        lockdown: lockdown,
        lessonVideoDuration: duration,
        videoProgress: videoProgress,
        startSeconds: learningCue?.start_seconds,
      }
      openDialog("topVideoBanner", data).then((values) => {
        setLearningCueContent(learningCue.id, {
          topBanner: values,
        })
      })
    }
  }

  const handleBottomVideoBannerChipClick = () => {
    if (!lockdown || (lockdown && learningCue?.bottomBanner)) {
      const data = {
        ...learningCue?.bottomBanner,
        lockdown: lockdown,
        lessonVideoDuration: duration,
        videoProgress: videoProgress,
        startSeconds: learningCue?.start_seconds,
      }
      openDialog("bottomVideoBanner", data).then((values) => {
        setLearningCueContent(learningCue.id, {
          bottomBanner: values,
        })
      })
    }
  }

  const handleFullscreenChipClick = () => {
    if (!lockdown || (lockdown && learningCue?.fullscreen)) {
      const data = {
        lockdown: lockdown,
        lessonVideoDuration: duration,
        videoProgress: videoProgress,
        startSeconds: learningCue?.start_seconds,
      }
      if (learningCue?.fullscreen_seconds) {
        data.fullscreen_seconds = learningCue?.fullscreen_seconds
      }
      openDialog("fullscreen", data).then((values) => {
        setLearningCueContent(learningCue.id, {
          fullscreen: values === undefined ? 0 : 1,
          fullscreen_seconds:
            values === undefined ? null : values.fullscreen_seconds,
        })
      })
    }
  }

  const handleReadyCheckChipClick = () => {
    if (!lockdown || (lockdown && learningCue?.readyCheck)) {
      const data = {
        ...learningCue?.readyCheck,
        lockdown: lockdown,
        startSeconds: learningCue?.start_seconds,
      }
      openDialog("readyCheck", data).then((values) => {
        setLearningCueContent(learningCue.id, {
          readyCheck: values,
          force_pause: true,
        })
      })
    }
  }

  const handleNotesChipClick = () => {
    if (!lockdown || (lockdown && learningCue?.prefilledNotes)) {
      const data = {
        ...learningCue?.prefilledNotes,
        lockdown: lockdown,
        startSeconds: learningCue?.start_seconds,
      }
      openDialog("notes", data).then((values) => {
        setLearningCueContent(learningCue.id, {
          prefilledNotes: values,
        })
      })
    }
  }

  const handleCheckboxChange = (e) => {
    const checked = e.target.checked
    const name = e.target.name
    changeCheckedValue(learningCue.id, name, checked)
  }

  const [menuAnchorEl, setMenuAnchorEl] = useState(null)

  const handleOpenMenu = (event) => {
    setMenuAnchorEl(event.currentTarget)
  }

  const handleCloseMenu = () => {
    setMenuAnchorEl(null)
  }

  const handleMenuClick = (menuItem) => () => {
    if (menuItem === "remove") {
      removeLearningCue(learningCue.id)
    } else if (menuItem === "copy") {
      copyLearningCue(videoProgress, learningCue)
    }
    handleCloseMenu()
  }

  const handleFocusInput = () => {
    setRowFocused(true)
    // setHighlightedLearningCue(learningCue.id)
  }

  const handleBlurInput = () => {
    setRowFocused(false)
    if (internalValue !== "") {
      // a new value was set, call updateTime
      const startTime = dayjs("00:00:00", "HH:mm:ss")
      const timestamp = dayjs(internalValue, "HH:mm:ss")
      const seconds = timestamp.diff(startTime, "second")
      updateTime(seconds)
      setInternalValue("")
    }
    // setHighlightedLearningCue(null)
  }

  const showChip = (boolean) => {
    return !lockdown || boolean
  }

  const learningCueMediaName = useLearningCueMediaFileName(
    learningCue?.media?.key,
  )

  const [hovered, setHovered] = useState(false)

  const handleMouseEnter = () => {
    setHovered(true)
    setHighlightedLearningCue(learningCue.id)
  }

  const handleMouseLeave = () => {
    setHovered(false)
    setHighlightedLearningCue(null)
  }

  const isCueTypeWithForcePause =
    learningCue.hasOwnProperty("knowledgeCheck") ||
    learningCue.hasOwnProperty("sentimentQuestions") ||
    learningCue.hasOwnProperty("readyCheck")

  const canAddQuestionCue = () => {
    if (!isEmpty(sameTimestampCue)) {
      return (
        !sameTimestampCue.hasOwnProperty("knowledgeCheck") &&
        !sameTimestampCue.hasOwnProperty("sentimentQuestions") &&
        !sameTimestampCue.hasOwnProperty("readyCheck")
      )
    }
    return true
  }

  const canAddTimingCue = () => {
    if (!isEmpty(sameTimestampCue)) {
      return (
        !sameTimestampCue.hasOwnProperty("topBanner") &&
        !sameTimestampCue.hasOwnProperty("bottomBanner") &&
        !sameTimestampCue.hasOwnProperty("fullscreen") &&
        !sameTimestampCue.hasOwnProperty("media")
      )
    }
    return true
  }

  return (
    <motion.div
      {...animations}
      className={classnames(classes.learningCueItem, {
        // [classes.focused]: rowFocused,
      })}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <Box className={classes.inputBox}>
        <TimeField
          value={dayjs
            .duration(0, "s")
            .add(learningCue.start_seconds, "seconds")
            .format("HH:mm:ss")}
          onChange={(event, value) => {
            setInternalValue(value)
          }}
          input={
            <OutlinedInput
              className={classes.textField}
              variant="outlined"
              size="small"
              classes={{
                input: classes.input,
                notchedOutline: classes.none,
                focused: classes.inputFocused,
              }}
              onFocus={handleFocusInput}
              onBlur={handleBlurInput}
              disabled={lockdown}
            />
          }
          colon=":"
          showSeconds
        />
      </Box>
      <Box className={classes.chips}>
        <LearningCueChip
          locked={lockdown}
          learningCue={learningCue}
          type="Media"
          property="media"
          ActiveIcon={MediaIcon}
          disabled={Boolean(
            learningCue.topBanner ||
            learningCue.bottomBanner ||
            learningCue.fullscreen,
          )}
          hover={hovered}
          onClick={handleMediaChipClick}
        />
        <LearningCueChip
          locked={lockdown}
          learningCue={learningCue}
          type="Top Video Banner"
          property="topBanner"
          ActiveIcon={TopVideoBannerIcon}
          disabled={Boolean(
            learningCue.media ||
            learningCue.bottomBanner ||
            learningCue.fullscreen,
          )}
          hover={hovered}
          onClick={handleTopVideoBannerChipClick}
        />
        <LearningCueChip
          locked={lockdown}
          learningCue={learningCue}
          type="Bottom Video Banner"
          property="bottomBanner"
          ActiveIcon={BottomVideoBannerIcon}
          disabled={Boolean(
            learningCue.media ||
            learningCue.topBanner ||
            learningCue.fullscreen,
          )}
          hover={hovered}
          onClick={handleBottomVideoBannerChipClick}
        />
        <LearningCueChip
          locked={lockdown}
          learningCue={learningCue}
          type="Fullscreen"
          property="fullscreen"
          ActiveIcon={FullscreenIcon}
          disabled={Boolean(
            learningCue.knowledgeCheck ||
            learningCue.readyCheck ||
            learningCue.sentimentQuestions ||
            learningCue.media ||
            learningCue.topBanner ||
            learningCue.bottomBanner,
          )}
          hover={hovered}
          onClick={handleFullscreenChipClick}
        />
        <LearningCueChip
          locked={lockdown}
          learningCue={learningCue}
          type="Knowledge Check"
          property="knowledgeCheck"
          ActiveIcon={CheckIcon}
          disabled={Boolean(
            lesson.skippable ||
            learningCue.fullscreen ||
            learningCue.readyCheck ||
            learningCue.sentimentQuestions ||
            !canAddQuestionCue(),
          )}
          hover={hovered}
          onClick={handleKnowledgeCheckChipClick}
        />
        <LearningCueChip
          locked={lockdown}
          learningCue={learningCue}
          type="Sentiment Question"
          property="sentimentQuestions"
          ActiveIcon={SentimentQuestionIcon}
          disabled={Boolean(
            lesson.skippable ||
            learningCue.fullscreen ||
            learningCue.knowledgeCheck ||
            learningCue.readyCheck ||
            !canAddQuestionCue(),
          )}
          hover={hovered}
          onClick={handleSentimentQuestionClick}
        />
        <LearningCueChip
          locked={lockdown}
          learningCue={learningCue}
          type="Ready Check"
          property="readyCheck"
          ActiveIcon={ResumeIcon}
          hover={hovered}
          disabled={Boolean(
            lesson.skippable ||
            learningCue.fullscreen ||
            learningCue.media ||
            learningCue.knowledgeCheck ||
            learningCue.sentimentQuestions ||
            learningCue.topBanner ||
            learningCue.bottomBanner ||
            learningCue.prefilledNotes ||
            !canAddQuestionCue(),
          )}
          onClick={handleReadyCheckChipClick}
        />
        <LearningCueChip
          locked={lockdown}
          learningCue={learningCue}
          type="Notes"
          property="prefilledNotes"
          ActiveIcon={NotesIcon}
          hover={hovered}
          onClick={handleNotesChipClick}
        />
      </Box>
      <Box
        className={classes.chips}
        display="flex"
        justifyContent="flex-end"
        flexGrow={1}
      >
        <FormControlLabel
          control={
            <Checkbox
              className={classes.checkbox}
              checked={learningCue.force_pause}
              name="force_pause"
              onChange={handleCheckboxChange}
              disabled={isCueTypeWithForcePause || lockdown}
            />
          }
          label="Force pause"
        />
      </Box>
      <Box className={classes.buttons}>
        <IconButton
          size="small"
          className={classes.moreButton}
          onClick={handleOpenMenu}
          disabled={lockdown}
        >
          <MoreHorizIcon style={{ fontSize: 16 }} />
        </IconButton>
        <Menu
          id="simple-menu"
          anchorEl={menuAnchorEl}
          keepMounted
          open={Boolean(menuAnchorEl)}
          onClose={handleCloseMenu}
        >
          <MenuItem onClick={handleMenuClick("remove")}>
            <ListItemIcon className={classes.listItemIcon}>
              <RemoveCircleOutlineIcon className={classes.removeIcon} />
            </ListItemIcon>
            <ListItemText primary="Remove" />
          </MenuItem>
          <MenuItem onClick={handleMenuClick("copy")}>
            <ListItemIcon className={classes.listItemIcon}>
              <FileCopyOutlinedIcon style={{ fontSize: 16 }} />
            </ListItemIcon>
            <ListItemText primary="Copy" />
          </MenuItem>
        </Menu>
      </Box>
    </motion.div>
  )
}

LearningCueItem.propTypes = {
  learningCue: PropTypes.object,
  updateTime: PropTypes.func,
  lockdown: PropTypes.bool,
  sameTimestampCue: PropTypes.object,
}

export default LearningCueItem
